import { Channel, Socket } from "phoenix";
import {
  IAppMessageHandlers,
  IAppMessageResponse,
  handleJoinResponse,
  setupChannelListeners,
} from "common/api/appMessage.service";
import { AppMessageAsyncActions } from "common/reducers/appMessage.async.actions";
import { Deferred } from "common/helpers/deferred";
import { accessUid } from "common/selectors/auth.selectors";
import { Nullable } from "common/types";
import { RootDispatchType, store } from "common/store";
import { authService } from "common/services/Auth.service";

export interface IAppMessageChannelOptions {
  dispatch: RootDispatchType;
  /** The socket to ride, or nothing while there is no live session to join through. */
  socket: () => Nullable<Socket>;
  leaveTimeout: number;
}

/**
 * Rejects a call made against a channel generation which has since been replaced — it belongs to the
 * previous access, so neither its push nor its reply may reach the new channel.
 */
export class SupersededChannelError extends Error {
  constructor() {
    super("The app_messages channel was replaced before the queued call ran");
    this.name = "SupersededChannelError";
  }
}

export const isSupersededChannelError = (error: unknown): boolean => error instanceof SupersededChannelError;

/** The `app_messages:<accessUid>` channel and the join its calls queue behind. */
export class AppMessageChannel {
  private channel: Nullable<Channel>;
  private accessGeneration = 0;
  private ready: Nullable<Deferred<Channel>>;
  private joinTimer: Nullable<ReturnType<typeof setTimeout>>;
  private joinTimeout = 15_000;
  private joined = false;
  private dispatch: RootDispatchType;
  private socket: () => Nullable<Socket>;
  private leaveTimeout: number;

  constructor(options: IAppMessageChannelOptions) {
    this.dispatch = options.dispatch;
    this.socket = options.socket;
    this.leaveTimeout = options.leaveTimeout;
  }

  /**
   * Resolves with the channel once the join lands, so a call made before the socket is up queues
   * rather than failing. Rejects if the join is refused, if it does not land within
   * `joinTimeout`, or if the generation it was made against is replaced.
   *
   * @returns
   */
  public whenReady(): Promise<Channel> {
    if (this.channel && this.joined) return Promise.resolve(this.channel);

    if (this.ready) return this.ready.promise;

    const ready = this.ensureReady();
    if (!this.channel) void this.join();

    return ready;
  }

  /**
   * Runs a push against the joined channel, rejecting if the access it was made for was replaced
   * before the push settled — phoenix delivers a reply to a channel it has already left, and lets a
   * stranded push's timeout fire long afterwards.
   *
   * @param run
   * @returns
   * @throws {SupersededChannelError}
   */
  public async call<T>(run: (channel: Channel) => Promise<T>): Promise<T> {
    const generation = this.accessGeneration;
    const channel = await this.whenReady();

    try {
      const result = await run(channel);

      if (this.accessGeneration !== generation) throw new SupersededChannelError();

      return result;
    } catch (error) {
      if (this.accessGeneration !== generation) throw new SupersededChannelError();

      throw error;
    }
  }

  public async join(allowReconnect = false): Promise<void> {
    // Phoenix buffers a join until the socket opens, so this must not gate on `isConnected()`.
    const socket = this.socket();
    if (!socket) return;

    const uid = accessUid(store.getState()) || authService.getAccessUid();
    // Phoenix's `app_messages:*` pattern needs at least one character after the colon; the server
    // reads "_" as the user's home access.
    const topic = `app_messages:${uid || "_"}`;

    if (this.channel) {
      if (!allowReconnect || this.channel.topic === topic) return;

      this.leaveChannel(true);
    }

    const channel = socket.channel(topic);
    const handlers = this.handlers(channel);
    setupChannelListeners(channel, handlers);

    this.channel = channel;
    // Created before the join so a call queued during it is fulfilled by this generation.
    this.ensureReady();

    return new Promise<void>((resolve) => {
      channel
        .join()
        .receive("ok", (response: IAppMessageResponse) => {
          if (this.settleReady(channel)) handleJoinResponse(response, handlers);
          resolve();
        })
        .receive("error", (reasons) => {
          this.failJoin(channel, reasons);
          resolve();
        })
        .receive("timeout", () => {
          this.failJoin(channel, new Error("A timeout occurred"));
          resolve();
        });
    });
  }

  /** Not a supersede: a disconnect is followed by a reconnect to the same access. */
  public leave(): void {
    this.leaveChannel(false);
  }

  private handlers(channel: Channel): IAppMessageHandlers {
    // Phoenix keeps delivering to a channel that is leaving, and a server broadcast carries no joinRef
    // for `isMember` to filter on.
    const current = () => this.channel === channel;

    return {
      onInitialMessages: (messages, metadata) => {
        if (current()) this.dispatch(AppMessageAsyncActions.receiveInitialMessages(messages, metadata));
      },
      onNewMessage: (message, metadata) => {
        if (current()) this.dispatch(AppMessageAsyncActions.receiveNewMessage(message, metadata));
      },
      onMessageUpdate: (message, metadata) => {
        if (current()) this.dispatch(AppMessageAsyncActions.receiveMessageUpdate(message, metadata));
      },
    };
  }

  private ensureReady(): Promise<Channel> {
    if (this.ready) return this.ready.promise;

    const ready = Deferred.defer<Channel>();
    this.ready = ready;

    this.joinTimer = setTimeout(
      () => this.rejectReady(new Error("Timed out waiting to join the messages channel")),
      this.joinTimeout,
    );

    ready.promise.catch(() => {});

    return ready.promise;
  }

  /**
   * @param channel
   * @returns whether the reply belongs to the generation that is still current
   */
  private settleReady(channel: Channel): boolean {
    if (this.channel !== channel) return false;

    this.joined = true;
    this.clearJoinTimer();
    this.ready?.resolve(channel);

    return true;
  }

  /**
   * Clears the promise so the next call or join starts a fresh generation.
   *
   * @param error
   */
  private rejectReady(error: unknown): void {
    this.clearJoinTimer();
    this.ready?.reject(error);
    this.ready = null;
  }

  private clearJoinTimer(): void {
    if (!this.joinTimer) return;

    clearTimeout(this.joinTimer);
    this.joinTimer = null;
  }

  private leaveChannel(supersede: boolean): void {
    if (!this.channel) return;

    if (supersede) this.accessGeneration += 1;

    this.channel.leave(this.leaveTimeout);
    this.channel = null;
    this.joined = false;

    if (supersede) this.rejectReady(new SupersededChannelError());
    else if (this.ready?.resolved) this.ready = null;
  }

  private failJoin(channel: Channel, error: unknown): void {
    channel.leave(this.leaveTimeout);

    if (this.channel !== channel) return;

    this.channel = null;
    this.joined = false;
    this.rejectReady(error);
  }
}
